import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../assets/icons/icon-close.svg";
import { ReactComponent as ExpandIcon } from "../assets/icons/icon-expand.svg";
import HotSpotModal from "./HotSpotModal";
import styles from "../../../styles/aprt.module.scss";
import { t } from "i18next";
import AssetLoadError from "./AssetLoadError";
import Loading from "../../Loading";

function PlanRendererHotSpotMode({
  svgContent,
  apartmentData,
  svgScale,
  dataUrl,
  selectedCompartmentInd,
  hoveredCompartmentInd,
}) {
  const { t } = useTranslation();
  const [selectedHotSpot, setSelectedHotSpot] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState({}); //TODO: use style ids
  const [modifiedSvgContent, setModifiedSvgContent] = useState(svgContent);
  const [isLoadError, setIsLoadError] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const svgContainerRef = useRef(null);

  const currentTier = apartmentData.compartments[0].ref_tier; //TODO: this should come form context; for now apartments are one level so current tier is first compartment tier
  const apartmentTiersData = apartmentData.tiers.find(
    (tier) => tier.ref_tier === currentTier
  );
  const apartmentCameras = apartmentTiersData.cameras;
  const apartmentHotSpots = apartmentCameras.filter(
    (camera) => camera.name.includes("id")
  );  //TODO: this filter should look for hotspot cameras, not exclude top_3D as there may be other cameras in the future

  const hotSpotSymbolRadius = 24;
  const hotSpotSymbolSectorRadius = 96;
  const numberOfStyles = 3; //TODO: temporary solution to use 3 styles, should be taken from context, and use style ids

  function calculateTargetAngle(target, position) {
    const targetX = target[0];
    const targetY = -target[1];
    const positionX = position[0];
    const positionY = -position[1];

    const deltaX = targetX - positionX;
    const deltaY = targetY - positionY;

    const angleRadians = Math.atan2(deltaY, deltaX);
    const angleDegrees = angleRadians * (180 / Math.PI);

    //This function returns angle in deg from -180 to 180 from positive x-axis, so use an icon that points to the right and rotate it by this angle
    return angleDegrees - 30; //-30 because this is half of the sector angle, and sector is not axially on X-axis //TODO: fix this in svg
  }

  const handleClose = () => {
    setSelectedHotSpot(null);
    setIsModalOpen(false);
    setIsLoadError(false);
  };

  const getImageUrl = () => {
    if (selectedHotSpot === null) return;
    const currentStyle = selectedStyle[selectedHotSpot] || 0; //TODO: use style ids
    return `${dataUrl}vis/interior/${selectedHotSpot}_${currentStyle}.png`; //TODO: refactor to use uuid, basepath form prop, and put files in correct dirs
  };

  const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  };

  const handleStyleChange = () => {
    setSelectedStyle((prevStyles) => {
      const currentStyle = prevStyles[selectedHotSpot] || 0; //TODO: use style ids
      const newStyle = (currentStyle + 1) % numberOfStyles; //TODO: use style ids
      return { ...prevStyles, [selectedHotSpot]: newStyle };
    });
  };

  useEffect(() => {
    if (!svgContent || !apartmentData) return;

    //TODO: what am i passing in svgContent? is it a string? do i always need to parse svgContent?
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
    const svgRoot = svgDoc.documentElement;
    const svgNS = "http://www.w3.org/2000/svg";

    const hotSpotsGroup = svgDoc.createElementNS(svgNS, "g");
    hotSpotsGroup.setAttribute("id", "hotSpots");

    const hotSpotSymbol = svgDoc.createElementNS(svgNS, "g");
    hotSpotSymbol.setAttribute("id", "hotspot-symbol");

    const sectorPath = svgDoc.createElementNS(svgNS, "path");
    sectorPath.setAttribute(
      "d",
      `M 0 0 L ${hotSpotSymbolSectorRadius} 0 A ${hotSpotSymbolSectorRadius} ${hotSpotSymbolSectorRadius} 0 0 1 ${
        hotSpotSymbolSectorRadius / 2
      } ${hotSpotSymbolSectorRadius * Math.sin((60 * Math.PI) / 180)} Z`
    );
    //TODO: add class form scss instead of below?
    sectorPath.setAttribute("stroke", "#f18d67");
    sectorPath.setAttribute("fill", "rgba(255, 255, 255, 0.6)");
    hotSpotSymbol.appendChild(sectorPath);

    // Create the largest circle with pulsating animation
    const circle1 = svgDoc.createElementNS(svgNS, "circle");
    circle1.setAttribute("cx", "0");
    circle1.setAttribute("cy", "0");
    circle1.setAttribute("r", `${hotSpotSymbolRadius}`);
    circle1.setAttribute("fill", "#f18d6799");
    const animate = svgDoc.createElementNS(svgNS, "animate");
    animate.setAttribute("attributeName", "r");
    animate.setAttribute(
      "values",
      `${hotSpotSymbolRadius};${
        hotSpotSymbolRadius * 1.5
      };${hotSpotSymbolRadius}`
    );
    animate.setAttribute("dur", "2s");
    animate.setAttribute("repeatCount", "indefinite");
    circle1.appendChild(animate);
    hotSpotSymbol.appendChild(circle1);

    // Create the second circle
    const circle2 = svgDoc.createElementNS(svgNS, "circle");
    circle2.setAttribute("cx", "0");
    circle2.setAttribute("cy", "0");
    circle2.setAttribute("r", `${hotSpotSymbolRadius * 0.75}`);
    circle2.setAttribute("fill", "none");
    circle2.setAttribute("stroke", "rgb(240, 240, 240)");
    circle2.setAttribute("stroke-width", "2");
    hotSpotSymbol.appendChild(circle2);

    // Create the third circle
    const circle3 = svgDoc.createElementNS(svgNS, "circle");
    circle3.setAttribute("cx", "0");
    circle3.setAttribute("cy", "0");
    circle3.setAttribute("r", `${hotSpotSymbolRadius * 0.5}`);
    circle3.setAttribute("fill", "currentColor");
    circle3.setAttribute("class", styles.hotspot_center);
    hotSpotSymbol.appendChild(circle3);

    apartmentHotSpots.forEach((spot) => {
      const hotSpot = hotSpotSymbol.cloneNode(true);

      hotSpot.setAttribute(
        "transform",
        `translate(${spot.position[0]} ${
          spot.position[1] * -1
        }) rotate(${calculateTargetAngle(spot.target, spot.position)}) scale(1)`
      );
      hotSpot.setAttribute("data-name", spot.name);

      hotSpotsGroup.appendChild(hotSpot);
    });

    svgRoot.appendChild(hotSpotsGroup);

    const serializer = new XMLSerializer();
    const newSvgContent = serializer.serializeToString(svgDoc);
    setModifiedSvgContent(newSvgContent);
  }, [svgContent, apartmentData]);

  const handleHotSpotClick = (spotName) => {
    setSelectedHotSpot(spotName);
    setIsModalOpen(true);
  };

  useEffect(() => {
    const svgContainer = svgContainerRef.current;
    if (svgContainer) {
      const hotSpots = svgContainer.querySelectorAll("#hotspot-symbol");
      hotSpots.forEach((hotSpot) => {
        const spotName = hotSpot.getAttribute("data-name");
        hotSpot.addEventListener("click", () => handleHotSpotClick(spotName));
      });
    }

    return () => {
      if (svgContainer) {
        const hotSpots = svgContainer.querySelectorAll("#hotspot-symbol");
        hotSpots.forEach((hotSpot) => {
          const spotName = hotSpot.getAttribute("data-name");
          hotSpot.removeEventListener("click", () =>
            handleHotSpotClick(spotName)
          );
        });
      }
    };
  }, [modifiedSvgContent]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    //TODO: check if not merge with above useEffect[svgContent]
    const svgContainer = svgContainerRef.current;
    if (!svgContainer) return;

    const compartmentPaths = svgContainer.querySelectorAll(
      ".compartments > g > path.compartment"
    );

    compartmentPaths.forEach((path) => {
      const compartmentNumber = Number(path.getAttribute("number"));
      //TODO: test listeners on mobile devices, maybe use onTouchStart instead of onClick
      if (
        compartmentNumber === selectedCompartmentInd ||
        compartmentNumber === hoveredCompartmentInd
      ) {
        path.classList.add(`${styles.svg_path_highlighted}`);
      }
      if (
        compartmentNumber != selectedCompartmentInd &&
        compartmentNumber != hoveredCompartmentInd
      ) {
        path.classList.remove(`${styles.svg_path_highlighted}`);
      }
    });
  }, [selectedCompartmentInd, hoveredCompartmentInd]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Preload image when selected hotspot or style changes
  useEffect(() => {
    if (selectedHotSpot !== null && isModalOpen) {
      const imageUrl = getImageUrl();
      if (imageUrl) {
        setIsImageLoading(true);
        setIsLoadError(false);
        
        preloadImage(imageUrl)
          .then(() => {
            setIsImageLoading(false);
          })
          .catch((error) => {
            console.error(error);
            setIsImageLoading(false);
            setIsLoadError(true);
          });
      }
    }
  }, [selectedHotSpot, selectedStyle, isModalOpen]);

  return (
    <>
      <div
        ref={svgContainerRef}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        dangerouslySetInnerHTML={{ __html: modifiedSvgContent }}
      />
      {selectedHotSpot !== null && isModalOpen && (
        // <>
        //   <div className={`${styles.overlaySVG} ${styles.viewPoint_wrapper}`}>
        //     <div
        //       className={styles.viewPoint}
        //       style={{ backgroundImage: `url(${getImageUrl()})` }}
        //       onClick={() => handleStyleChange(selectedHotSpot)}
        //     ></div>
        //     <div className={styles.viewPoint_buttons}>
        //       <button
        //         title="open in modal"
        //         className={styles.viewPoint_button}
        //         onClick={() => setIsModalOpen(true)}
        //       >
        //         <ExpandIcon />
        //       </button>
        //       <button
        //         tile="close view point"
        //         className={styles.viewPoint_button}
        //         onClick={handleClose}
        //       >
        //         <CloseIcon />
        //       </button>
        //     </div>
        //   </div>
        //   {isModalOpen && (
            <HotSpotModal
              isOpen={isModalOpen}
              // onClose={() => setIsModalOpen(false)}
              onClose={() => handleClose()}
            >
              {isImageLoading && (
                <div className={styles.loading_overlay}>
                  <Loading/>
                </div>
              )}
              {isLoadError && (
                <AssetLoadError
                  dataUrl={dataUrl}
                  loadingErrorMessage={t("img_load_error_1")}
                />
              )}
              {!isImageLoading && !isLoadError && (
                <div
                  className={styles.viewPoint}
                  style={{ backgroundImage: `url(${getImageUrl()})` }}
                  onClick={() => handleStyleChange(selectedHotSpot)}
                />
              )}
            </HotSpotModal>
          )}
        {/* </>
      )} */}
    </>
  );
}

export default PlanRendererHotSpotMode;

